import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import {useDispatch} from 'react-redux';
import moment from "moment/moment";
import {useLawTransactionsContext} from '../context/LawTransactionsContext';
import FilterTextField from '../../_common/components/FilterTextField';
import FiltersBar from '../../_common/components/FiltersBar';
import FilterSelect from "../../_common/components/FilterSelect";
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import {ButtonUploadFile} from "../../_common/components/ButtonUploadFile/ButtonUploadFile";
import * as actions from '../_redux/lawActions';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {generateExcelFile} from '../../_common/utils/excel';
import {lawTransactionsHeader} from '../../_common/constants/exportedFilesMainHeaders';
import {headersLawTransactions, headersLawUploadResult} from '../../_common/constants/exportedFilesHeaders';
import {transactionTypes} from "../constants/transactionTypes";
import type {LawTransactionsFilterValues, LawTransactionExcelItem, LawUploadResultExcelItem} from "../types";

type QueryParams = Record<string, any>

const prepareFilter = (queryParams: QueryParams, values: LawTransactionsFilterValues): QueryParams => {
  const {transactionsAgentID, transactionsAgentName, transactionType} = values

  const newQueryParams = { ...queryParams }
  const filter: QueryParams = {}

  if (Boolean(transactionsAgentID)) {
    filter.transactionsAgentID = transactionsAgentID
  } else {
    delete newQueryParams.transactionsAgentID
  }

  if (Boolean(transactionsAgentName)) {
    filter.transactionsAgentName = transactionsAgentName
  } else {
    delete newQueryParams.transactionsAgentName
  }

  if (Boolean(transactionType)) {
    filter.transactionType = transactionType
  } else {
    delete newQueryParams.transactionType
  }

  return { ...newQueryParams, ...filter }
}

export function LawTransactionsFilters(): JSX.Element {
  const dispatch: any = useDispatch()
  const {formatMessage} = useIntl()
  const context: any = useLawTransactionsContext()
  const props = useMemo(() => {
    return {
      queryParams: context.queryParams,
      setQueryParams: context.setQueryParams
    }
  }, [context])
  const applyFilter = (values: LawTransactionsFilterValues): void => {
    RequestCancellationManager.stopRequests(actions.geLawTransactionsListIDPrefix)

    const newQueryParams = prepareFilter(props.queryParams, values)

    if (!isEqual(newQueryParams, props.queryParams)) {
      props.setQueryParams(newQueryParams)
    }
  }
  const qs = new URLSearchParams(window.location.search);
  const transactionsAgentID: string = qs.get('transactionsAgentID') || '';
  const transactionsAgentName: string = qs.get('transactionsAgentName') || '';
  const transactionType: string = qs.get('transactionType') || '';
  const [loading, setLoading] = useState<boolean>(false);

  const handleExport = (): void => {
    setLoading(true)

    dispatch(actions.getLawTransactionsForExcelFile(props.queryParams))
      .then(prepareDataForExcel)
      .then((data: any) =>
        generateExcelFile(data, 'Lawsuit-transactions.xlsx', lawTransactionsHeader)
      )
      .catch((error: unknown) => {
        console.error(`Error: ${error}`)
        setLoading(false)
      })
      .finally(() => setLoading(false))
  }
  const prepareDataForExcel = (data: LawTransactionExcelItem[]) => {
    return data.map((item, index) => {
      const {
        agentName,
        agentId,
        amount,
        transactionType,
        internalCode,
        date,
      } = item as LawTransactionExcelItem

      return {
        [headersLawTransactions[0]]: index + 1,
        [headersLawTransactions[1]]: agentId,
        [headersLawTransactions[2]]: agentName,
        [headersLawTransactions[3]]: Boolean(date)
          ? moment(date).format('DD.MM.YYYY') : '',
        [headersLawTransactions[4]]: transactionType,
        [headersLawTransactions[5]]: internalCode,
        [headersLawTransactions[6]]: amount ? parseFloat(amount.toFixed(2)) : '',
      }
    })
  }

  const handleExportUploadResult = (data: LawUploadResultExcelItem[]): void => {
    if (data && data.length > 0) {
      const preparedData = prepareDataForUploadResultExcel(data);
      generateExcelFile(preparedData, 'Lawsuit-upload-results.xlsx', lawTransactionsHeader);
    }
  }
  const prepareDataForUploadResultExcel = (data: LawUploadResultExcelItem[]) => {
    return data.map((item, index) => {
      const {
        agentId,
        agentName,
        idNumber,
        tamalId,
        email,
        date,
        transactionType,
        internalCode,
        amount,
        status,
        notes,
      } = item as LawUploadResultExcelItem

      return {
        [headersLawUploadResult[0]]: index + 1,
        [headersLawUploadResult[1]]: agentId,
        [headersLawUploadResult[2]]: agentName,
        [headersLawUploadResult[3]]: idNumber,
        [headersLawUploadResult[4]]: tamalId,
        [headersLawUploadResult[5]]: email,
        [headersLawUploadResult[6]]: Boolean(date)
          ? moment(date).format('DD.MM.YYYY') : '',
        [headersLawUploadResult[7]]: transactionType,
        [headersLawUploadResult[8]]: internalCode,
        [headersLawUploadResult[9]]: amount,
        [headersLawUploadResult[10]]: status,
        [headersLawUploadResult[11]]: notes,
      }
    })
  }

  const prefix = 'law-filter'
  const containerClassnames = 'w-250px position-relative my-1 px-3';

  return (
    <Formik<LawTransactionsFilterValues>
      initialValues={{
        transactionsAgentName,
        transactionsAgentID,
        transactionType,
      }}
      onSubmit={applyFilter}
      enableReinitialize
    >
      {({ values, ...formikProps }) => (
        <FiltersBar
          filters={
            <div className="d-flex flex-wrap justify-content-center align-items-center mb-0 w-100">
              <FilterTextField
                fieldName="transactionsAgentID"
                type={'number'}
                placeholder={formatMessage({id: `${prefix}-agentID`, defaultMessage: 'Agent ID'})}
                value={values.transactionsAgentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="transactionsAgentName"
                placeholder={formatMessage({id: `${prefix}-agentName`, defaultMessage: 'Agent name'})}
                value={values.transactionsAgentName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterSelect
                fieldName={'transactionType'}
                valueName={values.transactionType}
                options={transactionTypes}
                placeholder={formatMessage({id: `${prefix}-transactionType`, defaultMessage: 'Type'})}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
          <>
            <ButtonExportToExcel exportFunc={handleExport} loading={loading}/>
            <ButtonUploadFile
              onFileSelected={async (file) => {
                setLoading(true);
                try {
                  const {result} = await actions.uploadLawTransactions(file);
                  handleExportUploadResult(result);
                  await dispatch(actions.getLawAgents(props.queryParams));
                  await dispatch(actions.getLawTransactions(props.queryParams));
                } finally {
                  setLoading(false);
                }
              }}
              accept={'.xls,.xlsx'}
              loading={loading}
            />
          </>
        }
        />
      )}
    </Formik>
  )
}